import type { ThemeBackgroundColors, ThemeTextColor } from '@rover/kibble/styles/themeTypes';

import { KibbleFormFieldComponentCommonProps } from '../../types';
import { SharedTypographyProps } from '../sharedTypography';

/** Styling configuration for select field states */
export type SelectFieldVariant = {
  /** Border color token */
  borderColor: string;
  /** Text color token */
  textColor: ThemeTextColor;
  /** Background color token */
  backgroundColor: ThemeBackgroundColors;
};

/** One option in the list. */
export type SelectFieldOptionConfig = {
  /** The option's text, shown in the list and in the field once it's chosen. */
  label: string;
  /** The value the form stores when this option is chosen. Each option needs its own. */
  value: string;
  /**
   * Shows the option grayed out, with the disabled fill and text color, but stops a click or tap
   * choosing it; on web the keyboard still can. Disabling the selected option throws outside
   * production.
   */
  disabled?: boolean;
};

/**
 * Common props for SelectField across web and React Native. Spread `configureField()` from
 * `useKibbleForm` onto the field to connect it to a form.
 */
export type SelectFieldCommonProps = KibbleFormFieldComponentCommonProps &
  SharedTypographyProps & {
    /** The options to choose from, in the order the list shows them. */
    options: SelectFieldOptionConfig[];
    /** The prompt shown in the field, in lighter text, until an option is chosen. */
    placeholder: string;
    /**
     * Called with the option's `value` each time someone chooses one, even the option already
     * selected.
     */
    onChange?: (selectedOption: SelectFieldOptionConfig['value']) => void;
    /** Width of the field, as a CSS length. Defaults to the full width of its container. */
    inputWidth?: string;
    /** Web only. React Native ignores it. */
    variantOptions?: {
      /**
       * Width of the open list, as a CSS length. Defaults to `280px`, whatever the field's
       * width. On React Native the list always matches the field's width.
       */
      dropdownWidth?: string;
    };
    /**
     * React Native only. The navigation bar's height, which offsets the open list to line up with
     * the field. Defaults to 44 on iOS and 0 on Android.
     */
    rxnNavBarHeightAdjust?: {
      /** The adjustment on iOS, in points. */
      ios?: number;
      /** The adjustment on Android, in density-independent pixels. */
      android?: number;
    };
  };

export const getVariantStyles = (
  hasError: boolean,
  isSelected: boolean,
  isDisabled?: boolean
): SelectFieldVariant => {
  if (isDisabled) {
    return {
      borderColor: 'neutral.200',
      textColor: 'disabled',
      backgroundColor: 'interactive.disabled',
    };
  }

  return {
    borderColor: hasError ? 'border.error' : 'interactive_border.primary_base',
    textColor: isSelected ? 'primary' : 'tertiary',
    backgroundColor: 'primary',
  };
};
